import type { Id } from "@convex/_generated/dataModel";
import { Button, Card } from "@buddy/ui";
import { createFileRoute, type ErrorComponentProps, Link, useRouter } from "@tanstack/react-router";
import { ConvexError } from "convex/values";
import { ChatRoom } from "@/features/chat/chat-room";

export const Route = createFileRoute("/_app/_main/chat/$roomId")({
  // focus names what the reader came for from the sidebar: "approval:<id>" scrolls to that card.
  validateSearch: (search: Record<string, unknown>): { focus?: string } =>
    typeof search["focus"] === "string" ? { focus: search["focus"] } : {},
  component: Page,
  errorComponent: ChatError,
});

// rooms.get throws not_found when the room is gone or the reader is not in it; say so only then.
// Anything else (a network drop, a render bug, a hot reload mid edit) gets a retry, so a
// passing failure never leaves the room looking deleted.
const isNotFound = (error: unknown) =>
  error instanceof ConvexError &&
  typeof error.data === "object" &&
  error.data !== null &&
  "code" in error.data &&
  error.data.code === "not_found";

function ChatError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const gone = isNotFound(error);
  const retry = () => {
    reset();
    void router.invalidate();
  };
  return (
    <Card className="flex-1 items-center justify-center gap-4">
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="text-[15px] font-medium leading-[18px] text-text">
          {gone ? "This chat is not available" : "This chat did not load"}
        </p>
        <p className="text-[13px] leading-[18px] tracking-normal text-text-2">
          {gone ? "It was removed, or you are not in it." : "Something went wrong on the way. Try again."}
        </p>
      </div>
      {gone ? (
        <Button variant="secondary" render={<Link to="/chat" />}>
          Back to chats
        </Button>
      ) : (
        <Button variant="secondary" onClick={retry}>
          Try again
        </Button>
      )}
    </Card>
  );
}

function Page() {
  const { roomId } = Route.useParams();
  const { focus } = Route.useSearch();
  return <ChatRoom roomId={roomId as Id<"rooms">} {...(focus === undefined ? {} : { focus })} />;
}
